import { Colli } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { notifyEntityCreated } from '@frontend/repository';
import { TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { Transaction, TransactionTriggerName, TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch } from '../../../redux/store';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { ensureTransactionCollisAndPackages } from '../../utils/ensure-transaction-collis-and-packages';
import {
    PackageProofOfIdentityRequirement,
    isReceiverCardProofRequirement,
    isSenderCardProofRequirement,
    resolvePackageProofOfIdentityRequirements
} from '../../utils/resolve-package-proof-of-identity-requirements';
import { syncWizardTransactionData } from '../../utils/sync-wizard-transaction-data';
import { useScreen } from '../useScreen';

interface ViewProps {
    transaction: Transaction | null;
    collis: Colli[] | null;
    senders: TransactionUser[] | null;
    receivers: TransactionUser[] | null;
    senderProofRequirements: PackageProofOfIdentityRequirement[];
    receiverProofRequirements: PackageProofOfIdentityRequirement[];
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screen: ScreenType) => void;
}

const useConfirmTransaction = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep, onCompleteWizard } = useScreen(props.wizard_id);
    const dispatch = useAppDispatch();
    const { refresh } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const [senders, setSenders] = useState<TransactionUser[] | null>(store?.data.senders || null);
    const [receivers, setReceivers] = useState<TransactionUser[] | null>(store?.data.receivers || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const sendersLoadedRef = useRef(false);
    const receiversLoadedRef = useRef(false);
    const transactionId = store?.data.transaction?.id;

    const proofRequirements = useMemo(
        () => resolvePackageProofOfIdentityRequirements(store),
        [store?.data.packages, store?.data.pendingPackages, store?.data.dropOffRequireProofOfIdentity, store?.data.pickUpRequireProofOfIdentity]
    );

    const senderProofRequirements = useMemo(() => proofRequirements.filter((requirement) => isSenderCardProofRequirement(requirement)), [proofRequirements]);

    const receiverProofRequirements = useMemo(
        () => proofRequirements.filter((requirement) => isReceiverCardProofRequirement(requirement)),
        [proofRequirements]
    );

    useEffect(() => {
        if (store?.data.senders?.length) {
            setSenders(store.data.senders);
            return;
        }

        if (sendersLoadedRef.current || senders !== null) return;

        if (transactionId) {
            sendersLoadedRef.current = true;
            TransactionUserClient.fetchTransactionUsers({ transaction_id: transactionId, type: TransactionUserType.SENDER }).then((res) =>
                setSenders(res.results)
            );
        }
    }, [senders, store?.data.senders, transactionId]);

    useEffect(() => {
        if (store?.data.receivers?.length) {
            setReceivers(store.data.receivers);
            return;
        }

        if (receiversLoadedRef.current || receivers !== null) return;

        if (transactionId) {
            receiversLoadedRef.current = true;
            TransactionUserClient.fetchTransactionUsers({ transaction_id: transactionId, type: TransactionUserType.RECEIVER }).then((res) =>
                setReceivers(res.results)
            );
            return;
        }

        if (store?.data.receiver) {
            receiversLoadedRef.current = true;
            setReceivers([store.data.receiver]);
        }
    }, [receivers, store?.data.receiver, store?.data.receivers, transactionId]);

    const onSubmit = async () => {
        if (!store?.data.transaction || !props.wizard_id || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const { transaction, senders, receivers: synced, location } = await syncWizardTransactionData(store);
            const { collis, packages } = await ensureTransactionCollisAndPackages(store, transaction, location);

            updateWizardData({
                collis,
                packages,
                transaction,
                senders,
                receivers: synced,
                spotId: transaction.spot_id,
                slotId: transaction.slot_id,
                moduleId: transaction.module_id
            });

            const nextStep = store.steps.find((step) => step.screen === ScreenType.CONFIRM_TRANSACTION && step.trigger === ScreenTrigger.SUBMIT);
            const continuesToPrintLabels = nextStep?.next_screen === ScreenType.CONFIRM_TRANSACTION_PRINT_LABELS;

            if (!continuesToPrintLabels) {
                await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                    trigger: TransactionTriggerName.TRANSACTION_PROCESS
                });
                // Draft create already notified; re-notify after PROCESS so Active (non-draft) lists refetch.
                notifyEntityCreated(dispatch, EntityType.TRANSACTION);
                await refresh();
            }

            if (nextStep) navigateToNextStep(nextStep);
            else await onCompleteWizard();
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        transaction: store?.data.transaction || null,
        collis: store?.data.collis || null,
        senders,
        receivers,
        senderProofRequirements,
        receiverProofRequirements,
        submitDisabled: !store || !props.wizard_id || !store.data.transaction || isSubmitting,
        onSubmit,
        onBack
    };
};

export default useConfirmTransaction;
